import { Accordion, AccordionItem } from '@carbon/react';
import type { ReactNode } from 'react';
import { CdFilterGroup, type FilterOption } from '../../molecules/CdFilterGroup';
import { CdSidePanel } from '../CdSidePanel';
import styles from './CdFilterPanel.module.scss';

export interface OptionGroup {
  id: string;
  title: string;
  options: FilterOption[];
}

/** A group whose body is any control (date inputs, a dropdown…) instead of checkboxes. */
export interface ContentGroup {
  id: string;
  title: string;
  content: ReactNode;
}

export type FilterPanelGroup = OptionGroup | ContentGroup;

export interface CdFilterPanelProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in order; option groups become checkbox lists, content groups render as given. */
  groups: FilterPanelGroup[];
  onOptionChange: (groupId: string, optionId: string, checked: boolean) => void;
  /** Omit to hide the "Clear filters" header action (e.g. Manage columns). */
  onClearAll?: () => void;
  title?: string;
  clearLabel?: string;
  /** Group ids that start expanded. */
  defaultOpenGroups?: string[];
}

/**
 * Right-hand panel of accordion option groups. Used for both "Filters" and "Manage columns"
 * (see `ManageColumnsPanel`).
 */
export const CdFilterPanel = ({
  open,
  onClose,
  groups,
  onOptionChange,
  onClearAll,
  title = 'Filters',
  clearLabel = 'Clear filters',
  defaultOpenGroups = [],
}: CdFilterPanelProps) => {
  return (
    <CdSidePanel
      open={open}
      onClose={onClose}
      title={title}
      headerActions={
        onClearAll && (
          <button type="button" className={styles.clear} onClick={onClearAll}>
            {clearLabel}
          </button>
        )
      }
    >
      <Accordion className={styles.accordion}>
        {groups.map((group) =>
          'content' in group ? (
            <AccordionItem
              key={group.id}
              title={<span className={styles.groupTitle}>{group.title}</span>}
              open={defaultOpenGroups.includes(group.id) || undefined}
            >
              {group.content}
            </AccordionItem>
          ) : (
            <CdFilterGroup
              key={group.id}
              id={group.id}
              title={group.title}
              options={group.options}
              open={defaultOpenGroups.includes(group.id) || undefined}
              onOptionChange={(optionId, checked) => onOptionChange(group.id, optionId, checked)}
            />
          ),
        )}
      </Accordion>
    </CdSidePanel>
  );
};
